import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Pause, Play, XCircle, CheckCircle2, AlertTriangle, ShieldCheck, Mail, Download, RotateCcw,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getSendRuns, getSendRun, getSendRunRecipients, sendRunAction, exportSendRun,
} from '../../../api/presend';
import { formatCount, formatMinutes } from '../format';
import { formatMelbourneDateTime } from '../../../utils/melbourneTime';

/**
 * Bulk sends (BE communications.presend): the list, and one send's approval,
 * live progress, delivery report, who was left out and its audit trail.
 * CG, Monday 13391839919 (1 Nov 2026).
 */
const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  queued: 'bg-blue-100 text-blue-800',
  sending: 'bg-cyan-100 text-cyan-800',
  paused: 'bg-gray-200 text-gray-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
};
const ACTIVE = ['draft', 'pending_approval', 'queued', 'sending', 'paused'];

const EVENT_LABELS = {
  created: 'Built', rebuilt: 'Rebuilt', submitted: 'Submitted', test_sent: 'Test sent', approved: 'Approved',
  rejected: 'Rejected', started: 'Started sending', paused: 'Paused', auto_paused: 'Paused automatically',
  resumed: 'Resumed', cancelled: 'Cancelled', completed: 'Finished', retry_failed: 'Retried failures',
};

const StatusPill = ({ status, label }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>
    {label}
  </span>
);

const when = (iso) => (iso ? formatMelbourneDateTime(iso) : '');
const errorText = (error, fallback) => error?.response?.data?.error || fallback;

const SendRunPage = () => {
  const { id } = useParams();
  return id ? <RunDetail id={id} /> : <RunList />;
};

const RunList = () => {
  const [runs, setRuns] = useState(null);
  const [awaitingMe, setAwaitingMe] = useState(false);
  useEffect(() => {
    setRuns(null);
    getSendRuns(awaitingMe ? { awaiting_me: 1 } : {})
      .then((res) => setRuns(res.data.results || []))
      .catch(() => { toast.error('Could not load sends'); setRuns([]); });
  }, [awaitingMe]);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Bulk sends</h1>
      <p className="text-sm text-gray-500 mb-4">
        Every bulk send, newest first. Large sends wait here for another super admin to approve them.
      </p>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input type="checkbox" checked={awaitingMe} onChange={(e) => setAwaitingMe(e.target.checked)} />
        Only sends waiting for my approval
      </label>
      {runs === null ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : runs.length === 0 ? (
        <div className="text-gray-500 text-sm">Nothing here.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Send</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">People</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <Link to={`/send-runs/${r.id}`} className="text-[#2A9DA8] font-medium hover:underline">
                      {r.name || r.kind_label}
                    </Link>
                    <span className="text-gray-500"> · {r.kind_label}</span>
                  </td>
                  <td className="px-4 py-2"><StatusPill status={r.status} label={r.status_label} /></td>
                  <td className="px-4 py-2 text-right">{formatCount(r.total_recipients)}</td>
                  <td className="px-4 py-2 text-gray-600">{when(r.created_at)}</td>
                  <td className="px-4 py-2 text-gray-600">{r.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const RECIPIENT_TABS = [
  ['', 'All'], ['queued', 'Remaining'], ['sent', 'Sent'], ['failed', 'Failed'], ['skipped', 'Skipped'],
  ['unknown', 'Unknown'], ['excluded', 'Left out'], ['cancelled', 'Cancelled'],
];

const Button = ({ tone = 'plain', className = '', ...props }) => {
  const tones = {
    primary: 'bg-[#55C5D0] text-white hover:bg-[#45B5C0]',
    plain: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
    danger: 'bg-white border border-red-300 text-red-700 hover:bg-red-50',
  };
  return (
    <button
      className={`inline-flex items-center gap-1 px-4 py-2 rounded-md disabled:opacity-50 ${tones[tone]} ${className}`}
      {...props}
    />
  );
};

const RunDetail = ({ id }) => {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('');
  const [page, setPage] = useState(1);
  const [recipients, setRecipients] = useState(null);

  const load = useCallback(() => getSendRun(id)
    .then((res) => setRun(res.data))
    .catch(() => toast.error('Could not load this send')), [id]);

  const loadRecipients = useCallback(() => getSendRunRecipients(id, { status: tab || undefined, page })
    .then((res) => setRecipients(res.data))
    .catch(() => setRecipients({ results: [], count: 0, num_pages: 1, page: 1 })), [id, tab, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const active = run && ACTIVE.includes(run.status);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => { load(); loadRecipients(); }, 5000);
    return () => clearInterval(t);
  }, [active, load, loadRecipients]);

  const act = async (action, confirmText, data) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(action);
    try {
      const res = await sendRunAction(id, action, data);
      setRun(res.data);
      loadRecipients();
      if (action === 'test-send') toast.success(`Test sent to ${res.data.test_sent_to}`);
    } catch (error) {
      toast.error(errorText(error, 'That did not work'));
    } finally {
      setBusy('');
    }
  };

  const download = async () => {
    try {
      const res = await exportSendRun(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `send-${run.kind}-${String(id).slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Could not export this send');
    }
  };

  if (!run) {
    return <div className="p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>;
  }

  const s = run.stats;
  const done = s.sent + s.failed + s.skipped + s.cancelled + s.unknown;
  const pct = s.total ? Math.round((done / s.total) * 100) : 0;
  const remainingMinutes = Math.ceil(s.queued / (run.rate_per_minute || 1));
  const beforeApproval = ['draft', 'pending_approval'].includes(run.status);
  const canManage = run.is_own;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <Link to="/send-runs" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> All sends
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{run.name || run.kind_label}</h1>
          <StatusPill status={run.status} label={run.status_label} />
          {run.version > 1 && <span className="text-xs text-gray-500">version {run.version}</span>}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {run.kind_label} · created {when(run.created_at)} by {run.created_by}
          {run.approved_by && <> · approved by {run.approved_by} {when(run.approved_at)}</>}
        </p>
      </div>

      {run.sending_switched_off && (
        <div className="flex gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {run.kind === 'portal_invite'
            ? 'Nothing is sending: this kind of email is switched off in Settings > Email.'
            : 'Nothing is sending: bulk sending is switched off for the whole CRM.'}
        </div>
      )}
      {run.params?.send_after && !run.started_at && ['queued', 'pending_approval'].includes(run.status) && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-900">
          Scheduled: it starts sending at {when(run.params.send_after)}
          {run.status === 'pending_approval' ? ', once it is approved' : ''}.
        </div>
      )}
      {run.params?.campaign_id && (
        <Link to={`/communications/campaigns/${run.params.campaign_id}`} className="text-sm text-[#2A9DA8] hover:underline">
          Open the campaign
        </Link>
      )}
      {run.status === 'paused' && run.paused_reason && (
        <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          <Pause className="w-4 h-4 mt-0.5 flex-shrink-0" /> {run.paused_reason}
        </div>
      )}
      {beforeApproval && run.requires_approval && (
        <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <div>
              Needs another super admin to approve it. Nothing has been sent. Approval needs a test of this
              version first{run.test_is_current ? '' : ', and there isn\'t one yet'}.
            </div>
            <ul className="list-disc ml-5 mt-1 text-amber-800">
              {run.review_reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
            {run.test_is_current && (
              <div className="mt-1">Test sent to {run.test_sent_to} by {run.test_sent_by} {when(run.test_sent_at)}.</div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {run.status === 'draft' && canManage && (
          <Button tone="primary" disabled={!!busy} onClick={() => act('submit')}>
            <CheckCircle2 className="w-4 h-4" /> {run.requires_approval ? 'Submit for approval' : 'Start sending'}
          </Button>
        )}
        {beforeApproval && (
          <Button disabled={!!busy} onClick={() => act('test-send')}>
            <Mail className="w-4 h-4" /> {busy === 'test-send' ? 'Sending test...' : 'Send me a test'}
          </Button>
        )}
        {run.can_approve && (
          <>
            <Button tone="primary" disabled={!!busy || !run.test_is_current}
              title={run.test_is_current ? '' : 'Send a test of this version first'}
              onClick={() => act('approve', `Approve sending to ${formatCount(s.queued)} people?`)}>
              <CheckCircle2 className="w-4 h-4" /> Approve
            </Button>
            <Button disabled={!!busy} onClick={() => {
              const note = window.prompt('Reject this send? Nothing will be sent. Reason (optional):');
              if (note !== null) act('reject', null, { note });
            }}>
              <XCircle className="w-4 h-4" /> Reject
            </Button>
          </>
        )}
        {['queued', 'sending'].includes(run.status) && (
          <Button disabled={!!busy} onClick={() => act('pause')}><Pause className="w-4 h-4" /> Pause</Button>
        )}
        {run.status === 'paused' && (
          <Button disabled={!!busy} onClick={() => act('resume')}><Play className="w-4 h-4" /> Resume</Button>
        )}
        {['draft', 'pending_approval', 'paused'].includes(run.status) && !run.started_at && canManage && (
          <Button disabled={!!busy}
            onClick={() => act('rebuild', 'Rebuild the list and message from scratch? Any approval and test will need doing again.')}>
            <RefreshCw className="w-4 h-4" /> Rebuild
          </Button>
        )}
        {s.failed > 0 && ['completed', 'paused', 'sending'].includes(run.status) && (
          <Button disabled={!!busy} onClick={() => act('retry-failed', `Try the ${s.failed} failed messages again?`)}>
            <RotateCcw className="w-4 h-4" /> Retry failed
          </Button>
        )}
        {ACTIVE.includes(run.status) && (
          <Button tone="danger" disabled={!!busy}
            onClick={() => act('cancel', 'Cancel this send? Messages not yet sent will not go out.')}>
            <XCircle className="w-4 h-4" /> Cancel send
          </Button>
        )}
        <Button onClick={download}><Download className="w-4 h-4" /> Export CSV</Button>
      </div>

      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex flex-wrap justify-between gap-2 text-sm mb-2">
          <span className="font-medium text-gray-800">{formatCount(done)} of {formatCount(s.total)} processed</span>
          {s.queued > 0 && run.status === 'sending' && (
            <span className="text-gray-500">about {formatMinutes(remainingMinutes)} left at {run.rate_per_minute}/min</span>
          )}
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-[#55C5D0] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 mt-5">
          <Stat label="Sent" value={s.sent} />
          <Stat label="Remaining" value={s.queued} />
          <Stat label="Failed" value={s.failed} tone={s.failed ? 'bad' : ''} />
          <Stat label="Skipped at send" value={s.skipped} />
          <Stat label="Outcome unknown" value={s.unknown} tone={s.unknown ? 'bad' : ''} />
          <Stat label="Cancelled" value={s.cancelled} />
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">After sending</h2>
        <div className="grid grid-cols-2 sm:grid-cols-7 gap-4">
          <Stat label="Delivered" value={s.delivered} />
          <Stat label="Opened" value={s.opened} />
          <Stat label="Clicked" value={s.clicked} />
          {s.registered !== undefined && (
            <Stat label="Registered" value={s.registered} tone={s.registered ? 'good' : ''} />
          )}
          <Stat label="Bounced" value={s.bounced} tone={s.bounced ? 'bad' : ''} />
          <Stat label="Unsubscribed" value={s.unsubscribed} />
          <Stat label="Spam reports" value={s.complaints} tone={s.complaints ? 'bad' : ''} />
        </div>
      </section>

      {run.excluded.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Left out when it was built</h2>
          <ul className="text-sm divide-y divide-gray-100">
            {run.excluded.map((row) => (
              <li key={row.reason} className="flex justify-between py-1.5">
                <span className="text-gray-700">{row.label}</span>
                <span className="font-medium">{formatCount(row.count)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bg-white border border-gray-200 rounded-lg">
        <div className="flex flex-wrap gap-1 px-4 pt-3 border-b border-gray-200">
          {RECIPIENT_TABS.map(([key, label]) => (
            <button key={key || 'all'} onClick={() => { setTab(key); setPage(1); }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === key ? 'border-[#55C5D0] text-gray-900 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">{run.channel === 'sms' ? 'Mobile' : 'Email'}</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(recipients?.results || []).map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-1.5">
                    {r.contact_id
                      ? <Link to={`/contacts/${r.contact_id}`} className="text-gray-900 hover:underline">{r.name || '(no name)'}</Link>
                      : (r.name || '(no name)')}
                  </td>
                  <td className="px-4 py-1.5 text-gray-600 break-all">{r.address}</td>
                  <td className="px-4 py-1.5 capitalize">{r.status === 'excluded' ? 'left out' : r.status}</td>
                  <td className="px-4 py-1.5 text-gray-500">
                    {r.status === 'sent' ? when(r.sent_at) : (r.reason || reasonLabel(run, r.reason_code))}
                  </td>
                </tr>
              ))}
              {recipients && recipients.results.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Nobody here.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {recipients && recipients.num_pages > 1 && (
          <div className="flex items-center justify-end gap-3 px-4 py-3 text-sm text-gray-600">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40">Previous</button>
            <span>Page {recipients.page} of {recipients.num_pages}</span>
            <button disabled={page >= recipients.num_pages} onClick={() => setPage(page + 1)} className="disabled:opacity-40">Next</button>
          </div>
        )}
      </section>

      {run.events?.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">History</h2>
          <ul className="text-sm space-y-1.5">
            {run.events.map((e, i) => (
              <li key={i} className="flex flex-wrap gap-x-2 text-gray-700">
                <span className="text-gray-500 w-44 flex-shrink-0">{when(e.at)}</span>
                <span className="font-medium">{EVENT_LABELS[e.action] || e.action}</span>
                {e.actor && <span>by {e.actor}</span>}
                {e.detail?.recipients !== undefined && <span>· {formatCount(e.detail.recipients)} people</span>}
                {e.detail?.to && <span>· to {e.detail.to}</span>}
                {e.detail?.reason && <span>· {e.detail.reason}</span>}
                {e.detail?.note && <span>· {e.detail.note}</span>}
                {run.version > 1 && <span className="text-gray-400">(v{e.version})</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

const reasonLabel = (run, code) => run.excluded.find((x) => x.reason === code)?.label || code || '';

const Stat = ({ label, value, tone }) => (
  <div>
    <div className={`text-2xl font-semibold ${tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-green-600' : 'text-gray-900'}`}>
      {formatCount(value || 0)}
    </div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

export default SendRunPage;
